/**
 * StudySync Fitbit Service
 * Handles all Fitbit integration API operations
 */

import { api, authenticatedApi } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import { fitbitConfig } from '../utils/fitbitConfig';
import type { FitbitAuthUrl, FitbitAuthData, ApiResponse } from '../types';

export class FitbitService {
  /**
   * Generate Fitbit OAuth authorization URL
   */
  static async getAuthUrl(
    participantId: string,
    studyCode: string
  ): Promise<ApiResponse<FitbitAuthUrl> & { authUrl?: string; state?: string; reauth?: boolean }> {
    return api.get(
      `${API_ENDPOINTS.FITBIT.AUTH_URL}?participantId=${participantId}&studyCode=${studyCode}`
    );
  }

  /**
   * Handle Fitbit OAuth callback
   */
  static async handleCallback(
    code: string,
    state: string,
    participantId?: string
  ): Promise<ApiResponse<FitbitAuthData>> {
    return api.post(API_ENDPOINTS.FITBIT.CALLBACK, {
      code,
      state,
      participantId,
    });
  }

  /**
   * Refresh Fitbit access token
   */
  static async refreshToken(
    participantId: string,
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.post(
      API_ENDPOINTS.FITBIT.REFRESH_TOKEN,
      { participantId },
      token
    );
  }

  /**
   * Extract Fitbit data for a participant
   */
  static async extractData(
    participantId: string,
    options: {
      dataTypes?: string[];
      startDate?: string;
      endDate?: string;
    },
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.post(
      API_ENDPOINTS.FITBIT.EXTRACT_DATA,
      {
        participantId,
        ...options,
      },
      token
    );
  }

  /**
   * Revoke Fitbit access for a participant
   */
  static async revokeAccess(
    participantId: string,
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.post(
      API_ENDPOINTS.FITBIT.REVOKE_ACCESS,
      { participantId },
      token
    );
  }

  /**
   * Get Fitbit authorization status for a participant
   */
  static async getStatus(
    participantId: string,
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.get(
      API_ENDPOINTS.FITBIT.STATUS(participantId),
      token
    );
  }

  /**
   * Get participants who need to re-authorize to grant new metrics (SpO2, breathing rate).
   * Requires researcher auth and study access.
   */
  static async getParticipantsNeedingReauth(
    studyId: string,
    token: string
  ): Promise<ApiResponse<{
    success: boolean;
    participants: Array<{
      participantId: string;
      participantEmail?: string;
      firstName?: string;
      lastName?: string;
      scope?: string;
    }>;
    studyCode?: string;
  }>> {
    return authenticatedApi.get(
      `${API_ENDPOINTS.FITBIT.PARTICIPANTS_NEEDING_REAUTH}?studyId=${encodeURIComponent(studyId)}`,
      token
    );
  }

  /**
   * Get re-auth authorization URL for a participant (prompt=consent so Fitbit shows consent again).
   * Researcher must be authenticated.
   */
  static async getReauthAuthUrl(
    participantId: string,
    studyCode: string,
    token: string
  ): Promise<ApiResponse<FitbitAuthUrl>> {
    return authenticatedApi.get(
      `${API_ENDPOINTS.FITBIT.AUTH_URL}?participantId=${encodeURIComponent(participantId)}&studyCode=${encodeURIComponent(studyCode)}&reauth=1`,
      token
    );
  }

  /**
   * Get memberSince (account creation date) for multiple participants
   */
  static async getMemberSince(
    participantIds: string[],
    token: string
  ): Promise<ApiResponse<{
    results: {
      [participantId: string]: {
        success: boolean;
        memberSince?: string;
        formattedDate?: string;
        error?: string;
      };
    };
  }>> {
    return authenticatedApi.post(
      API_ENDPOINTS.FITBIT.MEMBER_SINCE,
      { participantIds },
      token
    );
  }

  /**
   * Generate PKCE parameters for OAuth
   */
  static generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
  }

  /**
   * Generate code challenge from verifier
   */
  static async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }

  /**
   * Generate complete Fitbit OAuth URL with PKCE
   */
  static async generateOAuthUrl(
    studyCode: string,
    participantId?: string,
    redirectUri?: string
  ): Promise<{ url: string; codeVerifier: string }> {
    // Validate configuration
    if (!fitbitConfig.validate()) {
      throw new Error('Fitbit configuration is invalid');
    }

    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    const state = btoa(JSON.stringify({
      studyCode,
      participantId,
      codeVerifier,
      timestamp: Date.now(),
    }));

    // Use the unified configuration
    const finalRedirectUri = redirectUri || fitbitConfig.getConfig().redirectUri;
    
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: fitbitConfig.getConfig().clientId,
      redirect_uri: finalRedirectUri,
      scope: fitbitConfig.getConfig().scope,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      prompt: 'consent',
      state,
    });

    const url = `${fitbitConfig.getConfig().authorizationUrl}?${params.toString()}`;

    return { url, codeVerifier };
  }

  /**
   * Initiate Fitbit OAuth flow with popup
   */
  static async initiateFitbitOAuth(studyCode: string, participantId?: string): Promise<FitbitAuthData> {
    return new Promise<FitbitAuthData>(async (resolve, reject) => {
      try {
        const redirectUri = `${window.location.origin}/fitbit/callback`;
        const { url: fitbitOAuthUrl, codeVerifier } = await this.generateOAuthUrl(
          studyCode,
          participantId,
          redirectUri
        );

        // Store code verifier for callback retrieval
        localStorage.setItem('fitbit_code_verifier', codeVerifier);
        localStorage.setItem('fitbit_study_code', studyCode);

        // Create popup window
        const popup = window.open(
          fitbitOAuthUrl,
          'fitbit-oauth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        if (!popup) {
          reject(new Error('Popup blocked. Please allow popups for this site and try again.'));
          return;
        }

        // Listen for messages from popup
        const messageHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) {
            return;
          }

          if (event.data.type === 'FITBIT_OAUTH_SUCCESS') {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);

            // Store participant JWT in opener's sessionStorage so update-info works after navigate to /participant/info
            if (event.data.accessToken) {
              try {
                sessionStorage.setItem(
                  'studysync_participant_web_tokens',
                  JSON.stringify({
                    accessToken: event.data.accessToken,
                    refreshToken: event.data.refreshToken || null,
                    expiresAt: event.data.expiresAt || null,
                  })
                );
              } catch (e) {
                console.warn('Could not store participant tokens in opener', e);
              }
            }

            setTimeout(() => {
              if (!popup.closed) {
                popup.close();
              }
            }, 500);

            // Clean up stored verifier
            localStorage.removeItem('fitbit_code_verifier');
            localStorage.removeItem('fitbit_study_code');
            resolve(event.data.fitbitData);
          } else if (event.data.type === 'FITBIT_OAUTH_ERROR') {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);
            popup.close();
            localStorage.removeItem('fitbit_code_verifier');
            localStorage.removeItem('fitbit_study_code');
            reject(new Error(event.data.error || 'Fitbit authorization failed'));
          }
        };

        window.addEventListener('message', messageHandler);

        // Check if popup was closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);
            localStorage.removeItem('fitbit_code_verifier');
            localStorage.removeItem('fitbit_study_code');
            reject(new Error('Authorization cancelled by user'));
          }
        }, 3000);

      } catch (error) {
        reject(error);
      }
    });
  }
}

export default FitbitService;
